import { computed, ref, toValue } from "vue";
import { format, isValid, parseISO } from "date-fns";
import { toBoolean } from "#ce/utils/props";

/** The search params the filter always owns. `visit_types` joins them while the filter owns it. */
export const AVAILABILITY_FILTER_NAMES = Object.freeze([
  "availability",
  "available_date",
  "available_days",
  "available_times",
]);

export const VISIT_TYPES_FILTER_NAME = "visit_types";

/** `available_times` values, keyed by the time of day they name. */
export const AVAILABLE_TIMES = Object.freeze({ morning: "0-11", afternoon: "12-23" });

/** The date modes: each submits its own param (`available_date` for today and a specific day, `available_days` for days of the week). */
export const DATE_MODE = Object.freeze({ TODAY: "today", DAY_OF_WEEK: "day_of_week", SPECIFIC_DAY: "specific_day" });

/** The date modes, in the order the mode select offers them. */
export const DATE_MODES = Object.freeze(Object.values(DATE_MODE));

/** Day-of-week numbers, Sunday first, as `available_days` carries them. */
export const WEEKDAYS = Object.freeze([0, 1, 2, 3, 4, 5, 6]);

// Every param arrives from the URL, so each parser accepts only its well-formed values and drops
// anything else.
const TODAY_VALUE = /^today-tz:[+-](?:0\d|1[0-4]):[0-5]\d$/;
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const WEEKDAY_TOKEN = /^[0-6]$/;
const VISIT_TYPE_IDS = /^\d+(?:,\d+)*$/;

/**
 * The `available_date` value for the today mode: `today-tz:-07:00`.
 *
 * @param {Date} [now]
 * @returns {String}
 */
export function todayValue(now = new Date()) {
  return `today-tz:${format(now, "xxx")}`;
}

/** Whether `value` is a well-formed today value: `today-tz:` followed by a `±HH:MM` offset. */
export function isTodayValue(value) {
  return typeof value === "string" && TODAY_VALUE.test(value);
}

/**
 * Parses `available_date`: a well-formed today value or a real `YYYY-MM-DD` date is returned as
 * given; anything else (`"todayish"`, `"today-tz:+99:99"`, `"2026-13-40"`) is undefined.
 *
 * @param {*} value
 * @returns {String|undefined}
 */
export function parseAvailableDate(value) {
  if (typeof value !== "string") {
    return undefined;
  }
  if (isTodayValue(value)) {
    return value;
  }

  return CALENDAR_DATE.test(value) && isValid(parseISO(value)) ? value : undefined;
}

/**
 * Parses `available_days` into unique weekday numbers, keeping only exact `0`–`6` tokens:
 * `"1,2,2,7,1junk"` → `[1, 2]`.
 *
 * @param {String|Array|undefined} value
 * @returns {Number[]}
 */
export function parseDays(value) {
  const entries = Array.isArray(value) ? value.map(String) : String(value ?? "").split(",");
  const days = entries.filter((entry) => WEEKDAY_TOKEN.test(entry)).map(Number);

  return [...new Set(days)];
}

/**
 * Parses `available_times`: one of the `AVAILABLE_TIMES` values, or undefined.
 *
 * @param {*} value
 * @returns {String|undefined}
 */
export function parseAvailableTimes(value) {
  return Object.values(AVAILABLE_TIMES).includes(value) ? value : undefined;
}

/**
 * Parses `visit_types` into the comma-joined id list the param carries: `["1", "2"]` → `"1,2"`.
 * Anything but digits and commas is undefined.
 *
 * @param {String|Array|undefined} value
 * @returns {String|undefined}
 */
export function parseVisitTypes(value) {
  const joined = Array.isArray(value) ? value.join(",") : value;

  return typeof joined === "string" && VISIT_TYPE_IDS.test(joined) ? joined : undefined;
}

/**
 * The date mode the params describe, or undefined when they describe none.
 *
 * @param {{ availableDate?: String, availableDays?: Number[] }} params
 * @returns {String|undefined}
 */
export function dateModeFor({ availableDate, availableDays }) {
  if (isTodayValue(availableDate)) {
    return DATE_MODE.TODAY;
  }
  if (availableDate) {
    return DATE_MODE.SPECIFIC_DAY;
  }
  if (availableDays?.length > 0) {
    return DATE_MODE.DAY_OF_WEEK;
  }

  return undefined;
}

/**
 * The availability filter's selections and the `FormData` they submit.
 *
 * @param {Object} [options]
 * @param {import("vue").MaybeRefOrGetter<Boolean>} [options.ownsVisitTypes] - Whether this filter
 *   submits `visit_types`. Off where the standalone visit-type filter owns the param.
 * @param {import("vue").MaybeRefOrGetter<Boolean>} [options.requireSelection] - Whether filtering
 *   stays on only while a sub-filter is set. On where the online-booking switch is hidden.
 */
export function useAvailabilityFilterModel(options = {}) {
  const ownsVisitTypes = () => toValue(options.ownsVisitTypes ?? true);
  const requireSelection = () => toValue(options.requireSelection ?? false);

  const onlineBookingOnly = ref(false);
  const visitTypes = ref(undefined);
  const availableTimes = ref(undefined);
  const availableDate = ref(undefined);
  const availableDays = ref([]);

  // The mode the visitor picked while it still has no value, so an empty day-of-week grid or an
  // unpicked calendar stays on screen.
  const pendingDateMode = ref(undefined);

  const dateMode = computed(
    () =>
      dateModeFor({ availableDate: availableDate.value, availableDays: availableDays.value }) ?? pendingDateMode.value,
  );

  // A visit type counts only while this filter owns the param.
  const hasOwnedVisitType = computed(() => ownsVisitTypes() && Boolean(visitTypes.value));

  const hasSelection = computed(
    () =>
      hasOwnedVisitType.value ||
      Boolean(availableTimes.value) ||
      Boolean(availableDate.value) ||
      availableDays.value.length > 0,
  );

  const filterNames = computed(() =>
    ownsVisitTypes() ? [...AVAILABILITY_FILTER_NAMES, VISIT_TYPES_FILTER_NAME] : [...AVAILABILITY_FILTER_NAMES],
  );

  const model = computed(() => {
    const data = new FormData();

    if (!onlineBookingOnly.value) {
      return data;
    }

    data.set("availability", "true");

    if (availableDate.value) {
      data.set("available_date", availableDate.value);
    }
    if (availableDays.value.length > 0) {
      data.set("available_days", availableDays.value.join(","));
    }
    if (availableTimes.value) {
      data.set("available_times", availableTimes.value);
    }
    if (hasOwnedVisitType.value) {
      data.set(VISIT_TYPES_FILTER_NAME, visitTypes.value);
    }

    return data;
  });

  // Setting a sub-filter turns filtering on. Without the switch, filtering is on only while a
  // sub-filter is set.
  function settle(enabling) {
    if (enabling) {
      onlineBookingOnly.value = true;
    }
    if (requireSelection() && !hasSelection.value) {
      onlineBookingOnly.value = false;
    }
  }

  function setOnlineBookingOnly(value) {
    onlineBookingOnly.value = toBoolean(value);
    settle(false);
  }

  function setVisitTypes(value) {
    visitTypes.value = parseVisitTypes(value);
    settle(hasOwnedVisitType.value);
  }

  function setAvailableTimes(value) {
    availableTimes.value = parseAvailableTimes(value);
    settle(Boolean(availableTimes.value));
  }

  /** Switches the date mode, dropping the previous mode's value. */
  function setDateMode(mode) {
    availableDate.value = undefined;
    availableDays.value = [];
    pendingDateMode.value = DATE_MODES.includes(mode) ? mode : undefined;

    const isToday = mode === DATE_MODE.TODAY;

    if (isToday) {
      availableDate.value = todayValue();
    }

    settle(isToday);
  }

  function setAvailableDate(value) {
    availableDate.value = parseAvailableDate(value);
    settle(Boolean(availableDate.value));
  }

  function toggleDay(day, checked) {
    const value = Number.parseInt(day);
    const without = availableDays.value.filter((entry) => entry !== value);

    availableDays.value = checked && WEEKDAYS.includes(value) ? [...without, value].sort((a, b) => a - b) : without;
    settle(checked);
  }

  /**
   * Applies the params present in `values`, leaving the others as they are. A malformed value is
   * dropped. A date and days arriving together keep the date: it is the mode the panel shows, and
   * the days would narrow the search unseen. A named `availability` sets the switch: `"true"` turns
   * filtering on, and anything else (`"false"`, blank, null) turns it off even when a sub-filter is
   * set.
   *
   * @param {Object} [values]
   */
  function setFilterValues(values = {}) {
    pendingDateMode.value = undefined;
    let enabling = false;

    if ("available_date" in values) {
      availableDate.value = parseAvailableDate(values.available_date);
      enabling ||= Boolean(availableDate.value);
    }
    if ("available_days" in values) {
      availableDays.value = parseDays(values.available_days);
      enabling ||= availableDays.value.length > 0;
    }
    if (availableDate.value) {
      availableDays.value = [];
    }
    if ("available_times" in values) {
      availableTimes.value = parseAvailableTimes(values.available_times);
      enabling ||= Boolean(availableTimes.value);
    }
    if (VISIT_TYPES_FILTER_NAME in values) {
      visitTypes.value = parseVisitTypes(values.visit_types);
      enabling ||= hasOwnedVisitType.value;
    }
    if ("availability" in values) {
      onlineBookingOnly.value = toBoolean(values.availability);
      enabling = false;
    }

    settle(enabling);
  }

  function clear() {
    onlineBookingOnly.value = false;
    visitTypes.value = undefined;
    availableTimes.value = undefined;
    availableDate.value = undefined;
    availableDays.value = [];
    pendingDateMode.value = undefined;
  }

  return {
    onlineBookingOnly,
    visitTypes,
    availableTimes,
    availableDate,
    availableDays,
    dateMode,
    hasSelection,
    filterNames,
    model,

    setOnlineBookingOnly,
    setVisitTypes,
    setAvailableTimes,
    setDateMode,
    setAvailableDate,
    toggleDay,
    setFilterValues,
    clear,
  };
}
